import pipefitterApi from './pipefitterApi';

export type PipeEndKey = 'EndA' | 'EndB';

export interface RenamePipeIdPayload {
  id: number;
  projectNumber: string;
  newPipeID: string;
  updateEndA?: boolean;
  updateEndB?: boolean;
  newPipeEndIDA?: string;
  newPipeEndIDB?: string;
  /** Current pipeinfo.pipeEndID for End A (used in WHERE). */
  oldPipeEndIDA?: string;
  /** Current pipeinfo.pipeEndID for End B (used in WHERE). */
  oldPipeEndIDB?: string;
}

export interface RenamePipeIdS3Update {
  end: 'EndA' | 'EndB';
  oldPipeEndID: string;
  newPipeEndID: string;
  oldFileUrl?: string | null;
  newFileUrl?: string | null;
  oldS3Key?: string | null;
  newS3Key?: string | null;
  skipped?: boolean;
  skipReason?: string;
  error?: string;
}

export interface RenamePipeIdResult {
  id: number;
  projectNumber: string;
  oldPipeID: string;
  newPipeID: string;
  unchanged?: boolean;
  updatedEndA?: boolean;
  updatedEndB?: boolean;
  pipeEndIDA?: string | null;
  pipeEndIDB?: string | null;
  s3Updates?: RenamePipeIdS3Update[];
  s3Errors?: RenamePipeIdS3Update[];
}

export interface DeletePipeEndsPayload {
  id: number;
  projectNumber: string;
  ends: PipeEndKey[];
}

export interface DeletePipeEndsResult {
  id: number;
  projectNumber: string;
  pipeID: string;
  deletedEnds: PipeEndKey[];
  deletedPipeEndIDs: string[];
  softDeletedStatus: boolean;
  statusRemoved?: boolean;
  softDeletedPipeInfo?: boolean;
  pipeEndIDA?: string | null;
  pipeEndIDB?: string | null;
}

function unwrapData<T>(responseData: any): T {
  if (responseData?.data != null) return responseData.data as T;
  return responseData as T;
}

export async function renamePipeId(payload: RenamePipeIdPayload): Promise<RenamePipeIdResult> {
  const response = await pipefitterApi.put('/reports/rename-pipe-id', payload);
  if (response?.data?.status && response.data.status !== 200) {
    throw new Error(response.data.message || 'Failed to rename pipe ID');
  }
  return unwrapData<RenamePipeIdResult>(response.data);
}

export async function deletePipeEnds(payload: DeletePipeEndsPayload): Promise<DeletePipeEndsResult> {
  const response = await pipefitterApi.post('/reports/delete-pipe-ends', payload);
  if (response?.data?.status && response.data.status !== 200) {
    throw new Error(response.data.message || 'Failed to delete pipe ends');
  }
  return unwrapData<DeletePipeEndsResult>(response.data);
}

export interface UpdatePipeFlagsPayload {
  /** Prefer `ids` for single or bulk — one API call. */
  ids: number[];
  /** Only the fields being changed need to be sent. */
  isArchive?: 0 | 1;
  isFlange?: 0 | 1;
  isAnode?: 0 | 1;
}

export interface UpdatePipeFlagsResult {
  ids?: number[];
  id?: number;
  isArchive?: number;
  isFlange?: number;
  isAnode?: number;
  updatedCount?: number;
}

/** Dedicated flags update — does not touch status or ends. Supports bulk via `ids`. */
export async function updatePipeFlags(
  payload: UpdatePipeFlagsPayload
): Promise<UpdatePipeFlagsResult> {
  const response = await pipefitterApi.put('/reports/update-pipe-flags', payload);
  if (response?.data?.status && response.data.status !== 200) {
    throw new Error(response.data.message || 'Failed to update pipe flags');
  }
  return unwrapData<UpdatePipeFlagsResult>(response.data);
}
